import { Separator } from "@repo/shad/ui/separator"
import { SidebarTrigger } from "@repo/shad/ui/sidebar"
import { Link } from "@tanstack/react-router"
import { ChevronRight } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import type { SiteHeaderProps } from "../types"

const easeOutQuart = [0.165, 0.84, 0.44, 1] as const

export function SiteHeader({ breadcrumbs, actions }: SiteHeaderProps) {
  const shouldReduceMotion = useReducedMotion()

  return (
    <header className="flex h-(--header-height) border-b shrink-0 items-center gap-2  transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4" />
        <nav aria-label="Breadcrumb" className="min-w-0">
          <ol className="flex items-center gap-1.5 text-sm">
            <AnimatePresence initial={false} mode="popLayout">
              {breadcrumbs.map((crumb, index) => (
                <motion.li
                  key={crumb.key}
                  layout={shouldReduceMotion ? false : "position"}
                  className="flex min-w-0 items-center gap-1.5"
                  style={{ willChange: "transform" }}
                  initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.96, y: 4 }}
                  animate={shouldReduceMotion ? undefined : { opacity: 1, scale: 1, y: 0 }}
                  exit={shouldReduceMotion ? undefined : { opacity: 0, scale: 0.96, y: -4 }}
                  transition={{
                    duration: 0.15,
                    ease: easeOutQuart,
                    delay: shouldReduceMotion ? 0 : index * 0.03,
                  }}
                >
                  {index > 0 && (
                    <ChevronRight
                      className="size-3.5 text-muted-foreground/70"
                      aria-hidden="true"
                    />
                  )}
                  {crumb.href ? (
                    <Link
                      to={crumb.href as string}
                      className="max-w-[18rem] truncate text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span
                      aria-current={crumb.isCurrent ? "page" : undefined}
                      className="max-w-[18rem] truncate text-base font-medium text-foreground"
                    >
                      {crumb.label}
                    </span>
                  )}
                </motion.li>
              ))}
            </AnimatePresence>
          </ol>
        </nav>
        <div className="ml-auto flex items-center gap-2">{actions}</div>
      </div>
    </header>
  )
}
